import React, { useState } from 'react';
import {
  CreditCard, DollarSign, FileText, Shield, AlertTriangle, 
  TrendingUp, Calendar, Search, Filter, Plus, Eye, Edit, 
  Trash2, CheckCircle, XCircle, Clock3, Receipt, Building2,
  UserCheck, CreditCard as InsuranceCard
} from 'lucide-react';

interface InsuranceClaim {
  id: string;
  patientName: string;
  insuranceProvider: string;
  claimNumber: string;
  amount: number;
  status: 'pending' | 'approved' | 'rejected' | 'paid' | 'appealed';
  submissionDate: string;
  responseDate?: string;
  rejectionReason?: string;
}

interface BillingRecord {
  id: string;
  patientName: string;
  prescriptionId: string;
  amount: number;
  insuranceCoverage: number;
  patientResponsibility: number;
  status: 'unpaid' | 'partially_paid' | 'paid' | 'overdue';
  dueDate: string;
  lastPaymentDate?: string;
}

interface InsuranceProvider {
  id: string;
  name: string;
  type: 'commercial' | 'medicare' | 'medicaid' | 'private';
  coverageRate: number;
  activePatients: number;
  status: 'active' | 'inactive' | 'pending';
}

const mockInsuranceClaims: InsuranceClaim[] = [
  {
    id: 'IC-001',
    patientName: 'John Smith',
    insuranceProvider: 'Blue Cross Blue Shield',
    claimNumber: 'BCBS-2024-001',
    amount: 245.75,
    status: 'approved',
    submissionDate: '2024-01-10',
    responseDate: '2024-01-15'
  },
  {
    id: 'IC-002',
    patientName: 'Sarah Johnson',
    insuranceProvider: 'Aetna',
    claimNumber: 'AET-2024-002',
    amount: 189.50,
    status: 'pending',
    submissionDate: '2024-01-12'
  },
  {
    id: 'IC-003',
    patientName: 'Michael Brown',
    insuranceProvider: 'UnitedHealth',
    claimNumber: 'UHC-2024-003',
    amount: 312.00,
    status: 'rejected',
    submissionDate: '2024-01-08',
    responseDate: '2024-01-14',
    rejectionReason: 'Prior authorization required'
  },
  {
    id: 'IC-004',
    patientName: 'Emily Davis',
    insuranceProvider: 'Cigna',
    claimNumber: 'CIG-2024-004',
    amount: 156.25,
    status: 'paid',
    submissionDate: '2024-01-05',
    responseDate: '2024-01-12'
  }
];

const mockBillingRecords: BillingRecord[] = [
  {
    id: 'BR-001',
    patientName: 'John Smith',
    prescriptionId: 'RX-001',
    amount: 245.75,
    insuranceCoverage: 196.60,
    patientResponsibility: 49.15,
    status: 'paid',
    dueDate: '2024-01-20',
    lastPaymentDate: '2024-01-18'
  },
  {
    id: 'BR-002',
    patientName: 'Sarah Johnson',
    prescriptionId: 'RX-002',
    amount: 189.50,
    insuranceCoverage: 151.60,
    patientResponsibility: 37.90,
    status: 'unpaid',
    dueDate: '2024-01-25'
  },
  {
    id: 'BR-003',
    patientName: 'Michael Brown',
    prescriptionId: 'RX-003',
    amount: 312.00,
    insuranceCoverage: 0,
    patientResponsibility: 312.00,
    status: 'overdue',
    dueDate: '2024-01-15'
  }
];

const mockInsuranceProviders: InsuranceProvider[] = [
  {
    id: 'IP-001',
    name: 'Blue Cross Blue Shield',
    type: 'commercial',
    coverageRate: 80,
    activePatients: 45,
    status: 'active'
  },
  {
    id: 'IP-002',
    name: 'Aetna',
    type: 'commercial',
    coverageRate: 75,
    activePatients: 32,
    status: 'active'
  },
  {
    id: 'IP-003',
    name: 'Medicare',
    type: 'medicare',
    coverageRate: 85,
    activePatients: 28,
    status: 'active'
  },
  {
    id: 'IP-004',
    name: 'Medicaid',
    type: 'medicaid',
    coverageRate: 90,
    activePatients: 15,
    status: 'active'
  }
];

const getClaimStatusColor = (status: string) => {
  switch (status) {
    case 'pending': return 'border-yellow-500 bg-yellow-50 text-yellow-700';
    case 'approved': return 'border-blue-500 bg-blue-50 text-blue-700';
    case 'rejected': return 'border-red-500 bg-red-50 text-red-700';
    case 'paid': return 'border-green-500 bg-green-50 text-green-700';
    case 'appealed': return 'border-orange-500 bg-orange-50 text-orange-700';
    default: return 'border-gray-500 bg-gray-50 text-gray-700';
  }
};

const getBillingStatusColor = (status: string) => {
  switch (status) {
    case 'unpaid': return 'border-red-500 bg-red-50 text-red-700';
    case 'partially_paid': return 'border-yellow-500 bg-yellow-50 text-yellow-700';
    case 'paid': return 'border-green-500 bg-green-50 text-green-700';
    case 'overdue': return 'border-red-600 bg-red-100 text-red-800';
    default: return 'border-gray-500 bg-gray-50 text-gray-700';
  }
};

const getProviderTypeColor = (type: string) => {
  switch (type) {
    case 'commercial': return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'medicare': return 'bg-green-100 text-green-800 border-green-200';
    case 'medicaid': return 'bg-purple-100 text-purple-800 border-purple-200';
    case 'private': return 'bg-gray-100 text-gray-800 border-gray-200';
    default: return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

export function Billing() {
  const [searchTerm, setSearchTerm] = useState('');
  const [claimStatusFilter, setClaimStatusFilter] = useState('all');
  const [billingStatusFilter, setBillingStatusFilter] = useState('all');

  const filteredClaims = mockInsuranceClaims.filter(claim => {
    const matchesSearch = claim.patientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         claim.claimNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         claim.insuranceProvider.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = claimStatusFilter === 'all' || claim.status === claimStatusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const filteredBilling = mockBillingRecords.filter(billing => {
    const matchesSearch = billing.patientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         billing.prescriptionId.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = billingStatusFilter === 'all' || billing.status === billingStatusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const billingStats = {
    totalClaims: mockInsuranceClaims.length,
    pendingClaims: mockInsuranceClaims.filter(c => c.status === 'pending').length,
    totalBilling: mockBillingRecords.length,
    overdueBilling: mockBillingRecords.filter(b => b.status === 'overdue').length,
    totalRevenue: mockBillingRecords.reduce((sum, b) => sum + b.amount, 0),
    collectedRevenue: mockBillingRecords.filter(b => b.status === 'paid').reduce((sum, b) => sum + b.amount, 0)
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#4B0082] via-[#6B38FB] to-purple-600 rounded-3xl p-10 text-white shadow-2xl shadow-[#6B38FB]/25">
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-white/5 rounded-full blur-2xl animate-pulse delay-500"></div>
        </div>
        
        <div className="relative z-10">
          <div className="flex items-center space-x-4 mb-6">
            <div className="p-4 bg-white/20 backdrop-blur-sm rounded-2xl border border-white/30">
              <CreditCard className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-4xl font-black mb-2">Billing & Insurance</h1>
              <p className="text-xl text-purple-100">Manage claims, billing, and insurance processing</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <p className="text-purple-200 text-sm">Total Claims</p>
              <p className="text-2xl font-black">{billingStats.totalClaims}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <p className="text-purple-200 text-sm">Pending Claims</p>
              <p className="text-2xl font-black text-yellow-300">{billingStats.pendingClaims}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <p className="text-purple-200 text-sm">Total Revenue</p>
              <p className="text-2xl font-black">${billingStats.totalRevenue.toLocaleString()}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <p className="text-purple-200 text-sm">Collected</p>
              <p className="text-2xl font-black">${billingStats.collectedRevenue.toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-xl">
        <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search claims, patients, providers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-[#6B38FB]/20 focus:border-[#6B38FB] transition-all duration-300"
              />
            </div>
          </div>
          
          <div className="flex gap-3">
            <select
              value={claimStatusFilter}
              onChange={(e) => setClaimStatusFilter(e.target.value)}
              className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-[#6B38FB]/20 focus:border-[#6B38FB] transition-all duration-300"
            >
              <option value="all">All Claim Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="paid">Paid</option>
              <option value="appealed">Appealed</option>
            </select>
            
            <select
              value={billingStatusFilter}
              onChange={(e) => setBillingStatusFilter(e.target.value)}
              className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-[#6B38FB]/20 focus:border-[#6B38FB] transition-all duration-300"
            >
              <option value="all">All Billing Statuses</option>
              <option value="unpaid">Unpaid</option>
              <option value="partially_paid">Partially Paid</option>
              <option value="paid">Paid</option>
              <option value="overdue">Overdue</option>
            </select>
          </div>
          
          <button className="px-6 py-3 bg-gradient-to-r from-[#4B0082] to-[#6B38FB] text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-[#6B38FB]/25 transition-all duration-300 hover:scale-105 flex items-center space-x-2">
            <Plus className="w-5 h-5" />
            <span>New Claim</span>
          </button>
        </div>
      </div>

      {/* Insurance Claims Table */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-[#4B0082]/5 to-[#6B38FB]/5">
          <h3 className="text-xl font-bold text-gray-900">Insurance Claims</h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Claim ID</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Patient</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Provider</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Submission Date</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredClaims.map((claim) => (
                <tr key={claim.id} className="hover:bg-gray-50 transition-colors duration-200">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-semibold text-gray-900">{claim.claimNumber}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">{claim.patientName}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">{claim.insuranceProvider}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-semibold text-gray-900">${claim.amount.toLocaleString()}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full border ${getClaimStatusColor(claim.status)}`}>
                      {claim.status.charAt(0).toUpperCase() + claim.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">{claim.submissionDate}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex space-x-2">
                      <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Billing Records Table */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-[#4B0082]/5 to-[#6B38FB]/5">
          <h3 className="text-xl font-bold text-gray-900">Billing Records</h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Billing ID</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Patient</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Prescription</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Total Amount</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Insurance Coverage</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Patient Responsibility</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Due Date</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredBilling.map((billing) => (
                <tr key={billing.id} className="hover:bg-gray-50 transition-colors duration-200">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-semibold text-gray-900">{billing.id}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">{billing.patientName}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">{billing.prescriptionId}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-semibold text-gray-900">${billing.amount.toLocaleString()}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">${billing.insuranceCoverage.toLocaleString()}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-semibold text-gray-900">${billing.patientResponsibility.toLocaleString()}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full border ${getBillingStatusColor(billing.status)}`}>
                      {billing.status.replace('_', ' ').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">{billing.dueDate}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex space-x-2">
                      <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors">
                        <Receipt className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Insurance Providers */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-[#4B0082]/5 to-[#6B38FB]/5">
          <h3 className="text-xl font-bold text-gray-900">Insurance Providers</h3>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {mockInsuranceProviders.map((provider) => (
              <div key={provider.id} className="bg-gray-50 rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-all duration-300 hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-[#6B38FB]/10 rounded-xl">
                    <InsuranceCard className="w-6 h-6 text-[#6B38FB]" />
                  </div>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getProviderTypeColor(provider.type)}`}>
                    {provider.type.charAt(0).toUpperCase() + provider.type.slice(1)}
                  </span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">{provider.name}</h4>
                <div className="space-y-2 text-sm text-gray-600">
                  <p>Coverage Rate: {provider.coverageRate}%</p>
                  <p>Active Patients: {provider.activePatients}</p>
                  <p className="capitalize">Status: {provider.status}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-blue-100 rounded-xl">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">Submit Claim</h4>
              <p className="text-sm text-gray-600">File new insurance claim</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-green-100 rounded-xl">
              <Receipt className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">Generate Bill</h4>
              <p className="text-sm text-gray-600">Create patient billing</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-purple-100 rounded-xl">
              <TrendingUp className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">Analytics</h4>
              <p className="text-sm text-gray-600">Billing insights</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-orange-100 rounded-xl">
              <Shield className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">Compliance</h4>
              <p className="text-sm text-gray-600">Regulatory checks</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
